// Fase 12 Sprint 2 — Editar el borrador del precontrato (nueva versión).
// Reutiliza la lógica del wizard de NewCasePage pero hace PATCH en lugar de POST.
// Cada edición crea una nueva versión del intake en el backend (sin perder versiones
// previas).
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Disclaimer } from "../components/Disclaimer";
import { toast } from "sonner";
import { DEPARTAMENTOS } from "../lib/departamentos";

const ENTITY_OPTS = [
  ["", "Selecciona…"],
  ["nacional", "Nacional"],
  ["territorial_municipal", "Territorial municipal"],
  ["territorial_departamental", "Territorial departamental"],
  ["descentralizada", "Descentralizada"],
  ["empresa_industrial_comercial_estado", "Empresa industrial y comercial del Estado"],
  ["otra", "Otra"],
];

const GOODS_OPTS = [
  ["", "Selecciona…"],
  ["bienes", "Bienes"],
  ["servicios", "Servicios"],
  ["consultoria", "Consultoría"],
  ["interventoria", "Interventoría"],
  ["obra_publica", "Obra pública"],
  ["mixto", "Mixto"],
];

const FUND_OPTS = [
  ["", "Selecciona…"],
  ["presupuesto_nacional", "Presupuesto nacional"],
  ["sgp", "Sistema General de Participaciones"],
  ["sgr", "Sistema General de Regalías"],
  ["recursos_propios", "Recursos propios"],
  ["credito", "Crédito"],
  ["cooperacion_donacion", "Cooperación / Donación"],
  ["mixto", "Mixto"],
  ["otro", "Otro"],
];

const MODALITY_OPTS = [
  ["no_definida", "No estoy seguro"],
  ["licitacion_publica", "Licitación pública"],
  ["seleccion_abreviada", "Selección abreviada"],
  ["concurso_meritos", "Concurso de méritos"],
  ["contratacion_directa", "Contratación directa"],
  ["minima_cuantia", "Mínima cuantía"],
];

const URGENCIA_OPTS = [
  ["normal", "Normal"],
  ["alta", "Alta"],
  ["critica", "Crítica"],
];

export default function EditDraftPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [caseDoc, setCaseDoc] = useState(null);
  const [form, setForm] = useState({
    title: "",
    need_description: "",
    object_description: "",
    estimated_amount_cop: "",
    goods_services_works_type: "",
    expected_term_months: "",
    execution_location: "",
    execution_department: "",
    entity_type: "",
    funding_source: "",
    expected_modality: "no_definida",
    unspsc_codes: "",
    similar_process_links: "",
    deadline_date: "",
    urgencia: "normal",
    riesgos_percibidos: "",
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: c }, { data: intake }] = await Promise.all([
          api.get(`/cases/${id}`),
          api.get(`/cases/${id}/intake`),
        ]);
        if (!alive) return;
        setCaseDoc(c);
        setForm({
          title: c.title || "",
          need_description: intake.need_description || "",
          object_description: intake.object_description || c.objeto_contractual || "",
          estimated_amount_cop: String(intake.estimated_amount_cop ?? c.valor_estimado_cop ?? ""),
          goods_services_works_type: intake.goods_services_works_type || "",
          expected_term_months: intake.expected_term_months ? String(intake.expected_term_months) : "",
          execution_location: intake.execution_location || "",
          execution_department: intake.execution_department || "",
          entity_type: intake.entity_type || "",
          funding_source: intake.funding_source || "",
          expected_modality: intake.expected_modality || "no_definida",
          unspsc_codes: (intake.unspsc_codes || []).join(", "),
          similar_process_links: (intake.similar_process_links || []).join("\n"),
          deadline_date: intake.deadline_date || "",
          urgencia: intake.urgencia || "normal",
          riesgos_percibidos: intake.riesgos_percibidos || "",
        });
      } catch (e) {
        if (alive) {
          setError(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el borrador");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async (e) => {
    e?.preventDefault?.();
    setError("");
    setSubmitting(true);
    try {
      const valorNum = parseFloat(form.estimated_amount_cop);
      if (Number.isNaN(valorNum) || valorNum <= 0) {
        setError("El valor estimado debe ser mayor a 0.");
        setSubmitting(false);
        return;
      }
      const unspsc = form.unspsc_codes.split(",").map((s) => s.trim()).filter(Boolean);
      const links = form.similar_process_links.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      await api.patch(`/cases/${id}`, {
        title: form.title.trim(),
        objeto_contractual: form.object_description.trim(),
        valor_estimado_cop: valorNum,
        need_description: form.need_description.trim(),
        object_description: form.object_description.trim(),
        estimated_amount_cop: valorNum,
        goods_services_works_type: form.goods_services_works_type || null,
        expected_term_months: form.expected_term_months ? Number(form.expected_term_months) : null,
        execution_location: form.execution_location.trim() || null,
        execution_department: form.execution_department || null,
        entity_type: form.entity_type || null,
        funding_source: form.funding_source || null,
        expected_modality: form.expected_modality || "no_definida",
        unspsc_codes: unspsc,
        similar_process_links: links,
        deadline_date: form.deadline_date || null,
        urgencia: form.urgencia,
        riesgos_percibidos: form.riesgos_percibidos.trim() || null,
      });
      toast.success("Borrador actualizado (nueva versión creada).");
      navigate(`/cases/${id}`);
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || "No se pudo guardar la edición";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container-prose py-12">
        <div className="border border-gray-200 p-8 text-sm text-gray-500">Cargando borrador…</div>
      </div>
    );
  }

  return (
    <div className="container-prose py-12" data-testid="edit-draft-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-2">
          <Link to={`/cases/${id}`} className="text-sm text-gray-500 hover:text-primary">
            ← Volver al caso
          </Link>
        </div>
        <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">
          Editar borrador del precontrato
        </p>
        <h1 className="font-serif text-3xl text-gray-900 mb-2">{caseDoc?.title}</h1>
        <p className="text-sm text-gray-600 mb-6">
          Al guardar se creará una <strong>nueva versión</strong> del borrador.
          Las prevalidaciones, diagnósticos y kits previos siguen vinculados a su versión original.
        </p>

        {error && (
          <div className="mb-4 border border-destructive/30 bg-destructive/5 text-destructive p-3 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={onSave} className="space-y-6">
          <Field label="Título del caso">
            <Input value={form.title} onChange={(e) => setField("title", e.target.value)} required />
          </Field>

          <Field label="Necesidad (¿por qué se contrata?)">
            <Textarea
              value={form.need_description}
              onChange={(e) => setField("need_description", e.target.value)}
              rows={3}
            />
          </Field>

          <Field label="Objeto contractual">
            <Textarea
              value={form.object_description}
              onChange={(e) => setField("object_description", e.target.value)}
              rows={3}
              required
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Valor estimado (COP)">
              <Input
                type="number"
                min="1"
                value={form.estimated_amount_cop}
                onChange={(e) => setField("estimated_amount_cop", e.target.value)}
                required
              />
            </Field>
            <Field label="Plazo estimado (meses)">
              <Input
                type="number"
                min="0"
                value={form.expected_term_months}
                onChange={(e) => setField("expected_term_months", e.target.value)}
              />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Tipo de bien / servicio">
              <SelectField value={form.goods_services_works_type} onChange={(v) => setField("goods_services_works_type", v)} opts={GOODS_OPTS} />
            </Field>
            <Field label="Tipo de entidad">
              <SelectField value={form.entity_type} onChange={(v) => setField("entity_type", v)} opts={ENTITY_OPTS} />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Fuente de financiación">
              <SelectField value={form.funding_source} onChange={(v) => setField("funding_source", v)} opts={FUND_OPTS} />
            </Field>
            <Field label="Modalidad propuesta">
              <SelectField value={form.expected_modality} onChange={(v) => setField("expected_modality", v)} opts={MODALITY_OPTS} />
            </Field>
          </div>

          <Field label="Departamento de ejecución">
            <div data-testid="edit-draft-department-select">
              <SelectField value={form.execution_department} onChange={(v) => setField("execution_department", v)} opts={[["", "Selecciona…"], ...DEPARTAMENTOS.map((d) => [d, d])]} />
            </div>
          </Field>

          <Field label="Lugar de ejecución">
            <Input value={form.execution_location} onChange={(e) => setField("execution_location", e.target.value)} />
          </Field>

          <Field label="Códigos UNSPSC (separados por coma)">
            <Input value={form.unspsc_codes} onChange={(e) => setField("unspsc_codes", e.target.value)} placeholder="43211503, 43232605" />
          </Field>

          <Field label="Enlaces a procesos similares (uno por línea)">
            <Textarea
              value={form.similar_process_links}
              onChange={(e) => setField("similar_process_links", e.target.value)}
              rows={3}
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Fecha límite (opcional)">
              <Input type="date" value={form.deadline_date} onChange={(e) => setField("deadline_date", e.target.value)} />
            </Field>
            <Field label="Urgencia">
              <SelectField value={form.urgencia} onChange={(v) => setField("urgencia", v)} opts={URGENCIA_OPTS} />
            </Field>
          </div>

          <Field label="Riesgos percibidos (opcional)">
            <Textarea
              value={form.riesgos_percibidos}
              onChange={(e) => setField("riesgos_percibidos", e.target.value)}
              rows={3}
            />
          </Field>

          <div className="flex gap-3 pt-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm h-12"
              onClick={() => navigate(`/cases/${id}`)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-sm h-12 px-8"
              data-testid="edit-draft-save"
            >
              {submitting ? "Guardando…" : "Guardar nueva versión"}
            </Button>
          </div>
        </form>

        <div className="mt-8">
          <Disclaimer />
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function SelectField({ value, onChange, opts }) {
  return (
    <Select value={value || "__empty__"} onValueChange={(v) => onChange(v === "__empty__" ? "" : v)}>
      <SelectTrigger className="rounded-sm h-10"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
      <SelectContent>
        {opts.map(([v, l]) => (
          <SelectItem key={v || "_empty"} value={v || "__empty__"}>{l}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
